import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'

import { getCountries } from '../../../../api/countries/get-countries'
import { getFormulas } from '../../../../api/formula/get-formulas'
import { getPeriods } from '../../../../api/periods/get-periods'
import useTranslate from '../../../../lib/hooks/use-translate'
import {
  StaffPositionEnum,
  staffPositionEnumMapping
} from '../../../../lib/types/enums/staff/staff-position.enum'
import {
  StaffQualificationEnum,
  staffQualificationEnumMapping
} from '../../../../lib/types/enums/staff/staff-qualifications.enum'
import {
  DiplomaEnum,
  diplomaEnumMapping
} from '../../../../lib/types/enums/staff-application/diploma.enum'
import {
  PayScaleTypeEnum,
  payScaleTypeEnumMapping
} from '../../../../lib/types/enums/staff-pay-scale/pay-scale-type.enum'
import { StaffPayScaleInput } from '../../../../lib/types/models/staff-pay-scale/staff-pay-scale-input'
import { BaseFormProps } from '../../../../lib/types/utils'
import { getSubmitAction } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { getSelectedEnumOption } from '../../../../lib/utils/functions/misc.functions'
import { getTranslatedName } from '../../../../lib/utils/functions/translation.functions'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ControlledMultiSelectInput from '../../../elements/inputs/controlled-multi-select-input'
import ControlledSelectInput from '../../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../../elements/validation-buttons'
import { staffPayScaleFormSchema } from '../utils/staff-pay-scale-form-resolver'

const StaffPayScaleForm = ({
  defaultValues,
  ...validationButtonProps
}: BaseFormProps<StaffPayScaleInput, true>) => {
  const { t } = useTranslate()

  const form = useForm<StaffPayScaleInput>({
    resolver: zodResolver(staffPayScaleFormSchema),
    defaultValues,
    mode: FORM_MODE
  })

  const type = form.watch('type')

  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  const { data: periods } = useQuery({
    queryKey: ['periods'],
    queryFn: () => getPeriods({ version: 'BASIC' })
  })

  const { data: formulas } = useQuery({
    queryKey: ['formulas'],
    queryFn: () => getFormulas()
  })

  useEffect(() => {
    form.clearErrors()
  }, [type])

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className="flex gap-4 flex-col">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput {...field} type="text" label="NOM" required />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    label="Type de rémunération"
                    required
                    options={Object.values(PayScaleTypeEnum).map((value) => ({
                      label: payScaleTypeEnumMapping[value].label,
                      value
                    }))}
                    selectedOption={getSelectedEnumOption(field.value, payScaleTypeEnumMapping)}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          {type && (
            <>
              <div className="grid grid-cols-6 gap-4 row-span-3">
                <FormField
                  control={form.control}
                  name="position"
                  render={({ field }) => (
                    <FormItem className="flex-1 col-span-3">
                      <FormControl>
                        <ControlledSelectInput
                          {...field}
                          label="Poste"
                          options={Object.values(StaffPositionEnum)
                            .filter((value) => value !== StaffPositionEnum.AAT)
                            .map((value) => ({
                              label: t(staffPositionEnumMapping[value]),
                              value
                            }))}
                          selectedOption={getSelectedEnumOption(
                            field.value,
                            staffPositionEnumMapping,
                            t
                          )}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {type === PayScaleTypeEnum.DAILY_SALARY && (
                  <FormField
                    control={form.control}
                    name="qualifications"
                    render={({ field }) => (
                      <FormItem className="flex-1 col-span-3">
                        <FormControl>
                          <ControlledMultiSelectInput
                            {...field}
                            searchable
                            variant="primary"
                            label="Qualifications"
                            required
                            options={Object.values(StaffQualificationEnum).map((value) => ({
                              label: t(staffQualificationEnumMapping[value].label),
                              value
                            }))}
                            selectedOptions={field.value?.map((value) => ({
                              label: t(staffQualificationEnumMapping[value].label),
                              value
                            }))}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                )}

                <FormField
                  control={form.control}
                  name="countriesId"
                  render={({ field }) => (
                    <FormItem
                      className={cn('flex-1', {
                        'col-span-3': type === PayScaleTypeEnum.BONUS,
                        'col-span-2': type === PayScaleTypeEnum.DAILY_SALARY
                      })}
                    >
                      <FormControl>
                        <ControlledMultiSelectInput
                          {...field}
                          variant="primary"
                          label="Pays"
                          required
                          searchable
                          options={
                            countries?.data.map((country) => ({
                              label: country.name,
                              value: country.id
                            })) ?? []
                          }
                          selectedOptions={field.value?.map((id) => ({
                            label:
                              countries?.data.find((country) => country.id === id)?.name ?? '',
                            value: id
                          }))}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="formulasId"
                  render={({ field }) => (
                    <FormItem
                      className={cn('flex-1', {
                        'col-span-3': type === PayScaleTypeEnum.BONUS,
                        'col-span-2': type === PayScaleTypeEnum.DAILY_SALARY
                      })}
                    >
                      <FormControl>
                        <ControlledMultiSelectInput
                          {...field}
                          variant="primary"
                          label="Formules"
                          searchable
                          required
                          options={
                            formulas?.data.map((formula) => ({
                              label: getTranslatedName(formula),
                              value: formula.id
                            })) ?? []
                          }
                          selectedOptions={field.value?.map((id) => ({
                            label: getTranslatedName(
                              formulas?.data.find((formula) => formula.id === id)
                            ),
                            value: id
                          }))}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="periodsId"
                  render={({ field }) => (
                    <FormItem
                      className={cn('flex-1', {
                        'col-span-3': type === PayScaleTypeEnum.BONUS,
                        'col-span-2': type === PayScaleTypeEnum.DAILY_SALARY
                      })}
                    >
                      <FormControl>
                        <ControlledMultiSelectInput
                          {...field}
                          variant="warning"
                          label="Périodes"
                          searchable
                          required
                          options={
                            periods?.data.map((period) => ({
                              label: period.name,
                              value: period.id
                            })) ?? []
                          }
                          selectedOptions={field.value?.map((id) => ({
                            label: periods?.data.find((period) => period.id === id)?.name ?? '',
                            value: id
                          }))}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {type === PayScaleTypeEnum.BONUS && (
                  <>
                    <FormField
                      control={form.control}
                      name="diplomas"
                      render={({ field }) => (
                        <FormItem className={cn('flex-1 col-span-3')}>
                          <FormControl>
                            <ControlledMultiSelectInput
                              {...field}
                              searchable
                              variant="primary"
                              label="Diplômes"
                              required
                              options={Object.values(DiplomaEnum).map((value) => ({
                                label: t(diplomaEnumMapping[value]),
                                value
                              }))}
                              selectedOptions={field.value?.map((value) => ({
                                label: t(diplomaEnumMapping[value]),
                                value
                              }))}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="bonusFromFirstSlice"
                      render={({ field }) => (
                        <FormItem className={cn('flex-1 col-span-3')}>
                          <FormControl>
                            <ControlledInput
                              {...field}
                              type="number"
                              label="Montant (séjour 1 semaine)"
                              highlighted
                              required
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="bonusFromSecondSlice"
                      render={({ field }) => (
                        <FormItem className={cn('flex-1 col-span-3')}>
                          <FormControl>
                            <ControlledInput
                              {...field}
                              type="number"
                              label="Montant (séjour 2 semaines et plus)"
                              highlighted
                              required
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </>
                )}
              </div>

              {type === PayScaleTypeEnum.DAILY_SALARY && (
                <div className="flex gap-4">
                  <FormField
                    control={form.control}
                    name="dailySalaryFromFirstSlice"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormControl>
                          <ControlledInput
                            {...field}
                            type="number"
                            label="Montant 1ère tranche"
                            highlighted
                            required
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="dailySalaryFromSecondSlice"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormControl>
                          <ControlledInput
                            {...field}
                            type="number"
                            label="Montant 2e tranche"
                            highlighted
                            required
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              )}
            </>
          )}
          {type !== undefined && (
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormControl>
                      <ControlledInput {...field} label="Texte à afficher" type="textarea" />
                    </FormControl>
                  </FormItem>
                )
              }}
            />
          )}
        </div>

        <ValidationButtons {...validationButtonProps} />
      </form>
    </Form>
  )
}

export default StaffPayScaleForm
